<template>
    <DsModal
        class="legacy-email-modal"
        :is-open="isOpen"
        show-close
        size="med"
        :title="legacyEmailTitle"
        @close="closeModal"
    >
        <template #header>
            <DsFilledButton data-qa="create-or-edit-button" @click="editOrCreateLegacyEmail">
                {{ actionButtonLabel }}
            </DsFilledButton>
        </template>

        <template #default>
            <div class="goal-summary">
                <div class="goal-summary-text">
                    {{ $t('funnel.edit.campaign.legacy.email.summary.text') }}
                </div>
            </div>
        </template>
    </DsModal>
</template>

<script>
import {
    createLegacyEmail,
    updateLegacyEmail,
} from './legacy.email.api';
import { utils } from '../../../../utils/legacy-utils';

export const EVENT_READY_TOGGLE_CHANGED = 'email-ready-toggle-changed';
export const EVENT_EDITOR_LOADED = 'email-editor-loaded';
export const EVENT_CONFIG_DATA = 'load-data';
export const EVENT_SAVE_CONFIG_DATA = 'email-editor-save';
export const EVENT_RENAME = 'email-rename';
export const EMAIL_NAME = 'Legacy Email';

export default {
    props: {
        cell: {
            type: Object,
            default: () => ({
                value: {
                    marketingEmailId: '0',
                },
            }),
        },

        isOpen: {
            type: Boolean,
            required: true,
        },
    },
    emits: ['close-modal', 'update-cell-value', 'update-cell-name'],

    data() {
        return {
            loading: false,
            legacyEmailId: '0',
            editorInstance: null,
            isReady: false,
        };
    },

    computed: {
        actionButtonLabel() {
            return this.configuredEmail ? this.$t('funnel.edit.campaign.legacy.email.edit') : this.$t('funnel.edit.campaign.legacy.email.create');
        },
        legacyEmailTitle() {
            return this.cell.value?.name ?? this.$t('funnel.edit.campaign.legacy.email.title');
        },

        configuredEmail() {
            return this.legacyEmailId !== '0';
        },
    },

    mounted() {
        this.addEventListeners();
        this.name = this.cell?.value?.name || EMAIL_NAME;
        this.legacyEmailId = utils.removeLongSuffix(this.cell?.value?.marketingEmailId) || '0';
        this.isReady = this.cell.value?.ready === '1' ?? false;
    },

    unmounted() {
        this.removeEventListeners();
    },
    methods: {
        addEventListeners() {
            window.addEventListener(EVENT_READY_TOGGLE_CHANGED, this.readyToggleChanged);
            window.addEventListener(EVENT_SAVE_CONFIG_DATA, this.saveConfigData);
            window.addEventListener(EVENT_EDITOR_LOADED, this.editorReady);
            window.addEventListener(EVENT_RENAME, this.rename);
        },

        removeEventListeners() {
            window.addEventListener(EVENT_READY_TOGGLE_CHANGED, this.readyToggleChanged);
            window.addEventListener(EVENT_SAVE_CONFIG_DATA, this.saveConfigData);
            window.removeEventListener(EVENT_EDITOR_LOADED, this.editorReady);
            window.removeEventListener(EVENT_RENAME, this.rename);
        },

        editorReady() {
            this.postConfigMessage();
        },

        readyToggleChanged() {
            this.isReady = !this.isReady;
            this.saveCell(this.isReady);
        },

        async saveConfigData({ detail }) {
            const legacySaveEmailCommand = {
                fromKey: detail.fromKey,
                fromName: detail.fromName,
                fromEmail: detail.fromEmail,
                toKey: detail.toKey,
                toName: detail.toName,
                toEmail: detail.toEmail,
                subject: detail.subject,
                textBody: detail.textBody,
                htmlBody: detail.htmlBody,
                isCodeBuilder: detail.isCodeBuilder,
            };

            await updateLegacyEmail(this.legacyEmailId, legacySaveEmailCommand);
        },

        postConfigMessage() {
            this.editorInstance.postMessage({
                eventType: EVENT_CONFIG_DATA,
                data: {
                    cellName: this.name,
                    isReady: this.cell?.value?.ready,
                },
            }, '*');
        },

        closeModal() {
            if (this.editorInstance && !this.editorInstance.closed) {
                this.editorInstance.focus();
            } else {
                if (this.configuredEmail) {
                    this.saveCell();
                }
                this.$emit('close-modal');
            }
        },

        async editOrCreateLegacyEmail() {
            try {
                if (!this.configuredEmail) {
                    const emailTitle = this.legacyEmailTitle;

                    this.legacyEmailId = await createLegacyEmail(emailTitle);
                }
                this.editEmail();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(`Could not create email: ${error}`);
            }
        },

        editEmail() {
            if (this.editorInstance && !this.editorInstance.closed) {
                this.editorInstance.focus();
            } else {
                const editorUrl = `/app/emailFunnel/standalone/editor?contentType=email&parentId=${this.legacyEmailId}&emailTitle=${this.legacyEmailTitle}`;

                this.editorInstance = window.open(editorUrl, '_blank');
            }
        },

        rename({ detail }) {
            const newName = detail.name;

            if (newName) {
                const cell = { ...this.cell };

                cell.value = cell.value || {};
                cell.value.name = newName;
                this.$emit('update-cell-name', { cell });
            }
        },

        saveCell(isReady = true) {
            const cell = { ...this.cell };

            cell.value = cell.value || {};
            cell.value.marketingEmailId = this.legacyEmailId;
            this.$emit('update-cell-value', { cell, isReady, preventClose: true });
        },

    },
};
</script>
